import React, {
  Dispatch,
  FC,
  memo,
  SetStateAction,
  useState,
  useEffect,
} from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { getIsAdmin } from "Slices/currentUser";
import { getRawAccessToken } from "Utils/authTokens";
import {
  SecondaryNavigationBarStyled,
  SecondaryNavBarItems,
  SecondaryNavItemStyled,
  SecondaryNavTitleStyled,
} from "./styles";

interface SecondaryLinkItemType {
  id: string;
  text: string;
  link: string;
  onlyAdmin?: boolean;
}

const linksConfig: Array<SecondaryLinkItemType> = [
  {
    id: "audit",
    text: "Audit",
    link: "/vcms/audit",
  },
  {
    id: "vehicles",
    text: "Vehicles",
    link: "/vcms/vehicles",
  },
  {
    id: "vehicle_types",
    text: "Vehicle Types",
    link: "/vcms/vehicle_types",
  },
  {
    id: "location_types",
    text: "Location Types",
    link: "/vcms/location_types",
  },
  {
    id: "locations",
    text: "Locations",
    link: "/vcms/locations",
  },
  {
    id: "autonomy",
    text: "Autonomy",
    link: "/vcms/autonomy",
  },
  {
    id: "owner",
    text: "Owner",
    link: "/vcms/owner",
  },
  {
    id: "admin",
    text: "Admin",
    link: "/vcms/admin",
    onlyAdmin: true,
  },
];

export const SecondaryNavBarVCMS: FC<{
  isSecondaryNavVCMSShown: boolean;
  setIsSecondaryNavVCMSShown: Dispatch<SetStateAction<boolean>>;
}> = memo(({ isSecondaryNavVCMSShown, setIsSecondaryNavVCMSShown }) => {
  const { pathname } = useLocation();
  const _isAdmin = useSelector(getIsAdmin);
  const [isVCMSStaff, setIsVCMSStaff] = useState(true);

  useEffect(() => {
    const env = (window as Window & { _env_?: Record<string, string> })._env_;
    const apiUrl = (env?.REACT_APP_API_URL ?? "").replace(/\/$/, "");
    const jwt = getRawAccessToken();
    fetch(`${apiUrl}/bundledb/auth/me/`, {
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setIsVCMSStaff(data.is_staff ?? false);
      })
      .catch((_err: unknown) => {
        /* network errors are non-fatal */
      });
  }, []);

  const filteredLinks = linksConfig.filter(({ onlyAdmin: adminOnly }) => {
    if (adminOnly && !isVCMSStaff) {
      return false;
    }
    return true;
  });

  return (
    <SecondaryNavigationBarStyled
      isShown={isSecondaryNavVCMSShown}
      onMouseEnter={() => {
        setIsSecondaryNavVCMSShown(true);
      }}
      onMouseLeave={() => {
        setIsSecondaryNavVCMSShown(false);
      }}
    >
      <SecondaryNavBarItems>
        {filteredLinks.map(({ id, link, text }) => (
          <SecondaryNavItemStyled
            key={id}
            data-test-id={`nav-${id}`}
            to={link}
            $active={pathname === link}
            onClick={() => {
              setIsSecondaryNavVCMSShown(false);
            }}
          >
            <SecondaryNavTitleStyled>{text}</SecondaryNavTitleStyled>
          </SecondaryNavItemStyled>
        ))}
      </SecondaryNavBarItems>
    </SecondaryNavigationBarStyled>
  );
});
